/**
 * Zustand store for live audio chat state.
 */

import { create } from 'zustand'

export interface TranscriptEntry {
  id: string
  role: 'user' | 'assistant'
  text: string
  isFinal: boolean
  timestamp: Date
}

export interface ToolCallDisplay {
  name: string
  args: Record<string, unknown>
  status: 'running' | 'success' | 'error'
  result?: unknown
  durationMs?: number
}

interface LiveState {
  // Connection state
  isConnected: boolean
  isRecording: boolean
  isMuted: boolean
  selectedVoice: string

  // Content
  transcript: TranscriptEntry[]
  pendingTranscript: TranscriptEntry | null
  activeTools: ToolCallDisplay[]
  status: string | null

  // Actions
  setConnected: (connected: boolean) => void
  setRecording: (recording: boolean) => void
  setMuted: (muted: boolean) => void
  setVoice: (voice: string) => void

  addTranscript: (entry: TranscriptEntry) => void
  updateTranscript: (entry: TranscriptEntry) => void
  clearTranscript: () => void

  addTool: (tool: ToolCallDisplay) => void
  updateTool: (name: string, updates: Partial<ToolCallDisplay>) => void
  clearTools: () => void

  setStatus: (status: string | null) => void
  clearState: () => void
}

export const useLiveStore = create<LiveState>((set) => ({
  // Initial state
  isConnected: false,
  isRecording: false,
  isMuted: false,
  selectedVoice: 'Aoede',
  transcript: [],
  pendingTranscript: null,
  activeTools: [],
  status: null,

  // Connection actions
  setConnected: (connected) => set({ isConnected: connected }),
  setRecording: (recording) => set({ isRecording: recording }),
  setMuted: (muted) => set({ isMuted: muted }),
  setVoice: (voice) => set({ selectedVoice: voice }),

  // Transcript actions
  addTranscript: (entry) =>
    set((state) => ({
      transcript: [...state.transcript, entry],
      pendingTranscript: null,
    })),

  updateTranscript: (entry) =>
    set({ pendingTranscript: entry }),

  clearTranscript: () =>
    set({ transcript: [], pendingTranscript: null }),

  // Tool actions
  addTool: (tool) =>
    set((state) => ({
      activeTools: [...state.activeTools, tool],
    })),

  updateTool: (name, updates) =>
    set((state) => ({
      activeTools: state.activeTools.map((t) =>
        t.name === name ? { ...t, ...updates } : t
      ),
    })),

  clearTools: () => set({ activeTools: [] }),

  // Status
  setStatus: (status) => set({ status }),

  // Clear all state
  clearState: () =>
    set({
      isConnected: false,
      isRecording: false,
      transcript: [],
      pendingTranscript: null,
      activeTools: [],
      status: null,
    }),
}))
